'use client';

import { useRef, useState } from 'react';

import { Flex, Spacer, Stack, Visible } from '@backyard-ui/core';

import { type Price } from '@/infra/services/catalog/model';
import { type Optional } from '@/infra/services/catalog/service/model';
import { useServiceState } from '@/presenters/store/catalog/service';

import { PaymentMethodsModal } from '../../PaymentMethodsModal';
import { PriceTag } from '../../PriceTag';
import { getAdditionalPriceFromOptionals } from '../BuyBox/utils';

import { BuyButton } from './BuyButton';
import { ContractInfo } from './ContactInfo';
import { Optionals, type OptionalsRef, type OptionalsProps } from './Optionals';
import { TalkToExpertsLink } from './TalkToExpertsLink';

export default function BuyBox() {
  const {
    service: { unit, pricing, _id },
  } = useServiceState((state) => state);

  const optionalsRef = useRef<OptionalsRef>(null);

  const [selectedOptionals, setSelectedOptionals] = useState<Optional[]>([]);
  const [additionalPrice, setAdditionalPrice] = useState<Price>({
    to: 0,
    from: 0,
  });

  const handleOptionalsChange: OptionalsProps['onChange'] = (optionals) => {
    const additionalPrice = getAdditionalPriceFromOptionals(optionals);

    setSelectedOptionals(optionals);
    setAdditionalPrice(additionalPrice);
  };

  return (
    <Flex
      direction="column"
      align="flex-start"
      justify="flex-start"
      data-testid="buy-box"
    >
      <PriceTag
        pricing={pricing}
        unit={unit}
        additionalPrice={additionalPrice}
      />

      <Spacer size="5" axis="vertical" />

      <Stack
        direction="column"
        align="flex-start"
        spacing="5"
        className="w-full"
      >
        <PaymentMethodsModal />
        <Optionals
          onChange={handleOptionalsChange}
          ref={optionalsRef}
          itemId={_id}
        />
        <TalkToExpertsLink />
      </Stack>

      <Spacer size="5" axis="vertical" />

      <BuyButton
        optionalsRef={optionalsRef}
        selectedOptionals={selectedOptionals}
      />

      <Spacer size="14" axis="vertical" />

      <Visible above="md">
        <ContractInfo />
      </Visible>
    </Flex>
  );
}
